import { createSelector } from "reselect";
import {
  getDeadlineFromDateString,
  getDeadlineFromEpochTime,
} from "../../utils/utils";
import { removeCountryCode } from "../../utils/remove-country-code";
import { selectOrgsWithStatus } from "../org/org.selector";
import { RootState } from "../store";

export const selectTimerReducer = (state: RootState) => state.timer;

export const selectResendEnable = createSelector(
  [selectTimerReducer],
  (timer) => timer.resendEnable
);

export const selectModalOpen = createSelector(
  [selectTimerReducer],
  (timer) => timer.modalOpen
);

export const selectMinimised = createSelector(
  [selectTimerReducer],
  (timer) => timer.minimised
);

export const selectReservedData = createSelector(
  [selectTimerReducer],
  (timer) => timer.reservedData
);

export const selectReservedDeadline = createSelector(
  [selectReservedData],
  (reservedData) =>
    reservedData?.reserved_till
      ? getDeadlineFromEpochTime(reservedData?.reserved_till)
      : 0
);

export const selectPendingOrder = createSelector(
  [selectTimerReducer],
  (timer) => timer.pendingOrder
);

export const selectPendingDeadline = createSelector(
  [selectPendingOrder],
  (pendingOrder) => {
    return pendingOrder?.createdAt
      ? getDeadlineFromDateString(pendingOrder?.createdAt)
      : 0;
  }
);

export const selectDefaultDeadline = createSelector(
  [selectTimerReducer],
  (timer) => timer.deadline
);

export const selectDeadline = createSelector(
  [selectDefaultDeadline, selectReservedDeadline, selectPendingDeadline],
  (deadline, reservedDeadline, pendingDeadline) => {
    let resultDeadline;
    if (pendingDeadline) {
      resultDeadline = pendingDeadline;
    } else if (reservedDeadline) {
      resultDeadline = reservedDeadline;
    } else {
      resultDeadline = deadline;
    }
    return Date.now() > resultDeadline ? 0 : resultDeadline;
  }
);

export const selectSuccessOrderId = createSelector(
  [selectTimerReducer],
  (timer) => timer.successOrderId
);

export const selectSuccessVoipNumber = createSelector(
  [selectTimerReducer],
  (timer) => timer.successVoipNumber
);

export const selectOrder = createSelector(
  [selectTimerReducer],
  (timer) => timer.order
);

export const selectReferredSFNumber = createSelector(
  [selectPendingOrder, selectTimerReducer],
  (pendingOrder, timer) => {
    if (pendingOrder?.details?.voip_number) {
      return pendingOrder.details.voip_number;
    }
    return timer.referredSFNumber;
  }
);

export const selectReferredOrgId = createSelector(
  [selectPendingOrder, selectReservedData, selectTimerReducer],
  (pendingOrder, reservedData, timer) => {
    if (pendingOrder?.org_id) {
      return pendingOrder.org_id;
    } else if (reservedData?.metadata?.orgId) {
      return reservedData.metadata.orgId;
    }
    return timer.referredOrgId;
  }
);

export const selectNotificationOpen = createSelector(
  [selectTimerReducer],
  (timer) => timer.notificationOpen
);

export const selectNotificationOpenAndReferred = createSelector(
  [selectNotificationOpen, selectReferredSFNumber, selectReferredOrgId],
  (notificationOpen, referredSFNumber, orgId) =>
    notificationOpen && !!(referredSFNumber || orgId)
);

//Real decider whether notification is open or not
// export const selectNotificationVisible = createSelector(
//   [selectNotificationOpen, selectIsOnlyOneActiveOrg],
//   (notificationOpen, isOnlyOneActiveOrg) => {
//     return notificationOpen && !isOnlyOneActiveOrg;
//   }
// );

export const selectNotificationVisible = createSelector(
  [selectNotificationOpen],
  (notificationOpen) => {
    return notificationOpen;
  }
);

export const selectNotificationText = createSelector(
  [
    selectPendingOrder,
    selectReservedData,
    selectReferredSFNumber,
    selectReferredOrgId,
    selectOrgsWithStatus,
  ],
  (pendingOrder, reservedData, reservedSFNumber, referredOrgId, orgId) => {
    let text: string;
    if (pendingOrder?.details?.order_type === "RENEW") {
      text = `Payment in Progress (Renew ${pendingOrder?.details?.voip_number})`;
    } else if (pendingOrder?.details?.order_type === "PURCHASE") {
      text = "Payment in Progress (New Number)";
    } else if (reservedData?.voip_number) {
      text = "Payment in Progress (New Number)";
    }
    text = removeCountryCode({ phoneNumber: reservedSFNumber })
      ? `Payment in Progress (Renew ${removeCountryCode({
          phoneNumber: reservedSFNumber,
        })})`
      : "Payment in Progress (New Number)";
    return text;
  }
);

export const selectIsPaymentModalOpen = createSelector(
  [
    selectModalOpen,
    selectNotificationText,
    selectReferredSFNumber,
    selectReferredOrgId,
  ],
  (modalOpen, notificationText, referredSFNumber, referredOrgId) => {
    return (
      modalOpen && !!notificationText && !!(referredSFNumber || referredOrgId)
    );
  }
);
